import { useCallback, useEffect, useMemo, useState } from 'react';

import axios, { AxiosRequestConfig } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import AuthContainer from '../../context/AuthContainer';
import { getUrls } from '../../utils/url';
import { ClassStatus, TemplateRecord } from './types';
import {
    MOODLE_TEMPLATE_URL,
    TEMPLATES_CREATE,
    TEMPLATES_DELETE,
    TEMPLATES_GET,
    TEMPLATES_UPDATE
} from '../../config/apiPaths';

enum CreateStatus {
    INITIAL = 'INITIAL',
    LOADING = 'LOADING',
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR'
}

interface CreateTemplateInterface {
    templateName: string;
    description?: string | null;
}

interface UpdateTemplateInterface {
    sid: number;
    templateName: string;
    description?: string | null;
    status?: ClassStatus;
}

interface CreateTemplatePayload {
    class_description?: string;
    class_name: string;
    status?: ClassStatus;
}

interface CreateEditResponse {
    sid: number;
    token: string;
}

interface DeleteTemplateInterface {
    templateId: number;
    onError: () => void;
    onOptimisticUpdate?: () => void;
    onSuccess?: () => void;
}

interface UpdateTemplateStatusInterface extends DeleteTemplateInterface {
    status: ClassStatus;
}

const useTemplateContext = () => {
    const [draftTemplates, setDraftTemplates] = useState<TemplateRecord[]>([]);
    const [publishedTemplates, setPublishedTemplates] = useState<TemplateRecord[]>([]);
    const [createEditTemplateStatus, setCreateEditTemplateStatus] = useState<CreateStatus>(CreateStatus.INITIAL);
    const [saveAsDraftTemplateStatus, setSaveAsDraftTemplateStatus] = useState<CreateStatus>(CreateStatus.INITIAL);
    const [saveAsPublishedTemplateStatus, setSaveAsPublishedTemplateStatus] = useState<CreateStatus>(CreateStatus.INITIAL);
    const [createEditResponse, setCreateEditResponse] = useState<CreateEditResponse | null>(null);
    const [templateEditId, setTemplateEditId] = useState<number | null>(null);

    const { api } = getUrls();
    const { t: tCommon } = useTranslation('common');
    const { t } = useTranslation('templates');

    const { enqueueSnackbar } = useSnackbar();

    const history = useHistory();
    const { search } = useLocation();

    const { userUuid } = useContainer(AuthContainer);

    const getTemplates = useCallback(async (status: ClassStatus): Promise<any> => {
        if (!userUuid) return;

        try {
            const config: AxiosRequestConfig = {
                headers: {
                    token: userUuid
                },
                params: { status }
            };

            const { data } = await axios.get<TemplateRecord[]>(`${api}${TEMPLATES_GET}`, config);

            if (status === ClassStatus.Draft) {
                setDraftTemplates(data);
            }

            if (status === ClassStatus.Published) {
                setPublishedTemplates(data);
            }
        } catch (err) {
            enqueueSnackbar(tCommon('somethingWentWrong'), { variant: 'error' });
        }
    }, [api, enqueueSnackbar, tCommon, userUuid]);

    useEffect(() => {
        if (draftTemplates.length === 0) {
            getTemplates(ClassStatus.Draft);
        }
    }, [getTemplates, draftTemplates.length]);

    useEffect(() => {
        if (publishedTemplates.length === 0) {
            getTemplates(ClassStatus.Published);
        }
    }, [getTemplates, publishedTemplates.length]);

    const createTemplate = async ({ templateName, description }: CreateTemplateInterface): Promise<CreateStatus> => {
        setCreateEditTemplateStatus(CreateStatus.LOADING);

        try {
            const config: AxiosRequestConfig = {
                headers: {
                    token: userUuid as string
                }
            };

            const payload: CreateTemplatePayload = {
                class_name: templateName,
                status: ClassStatus.Draft
            };

            if (description) {
                payload.class_description = description;
            }

            const { data } = await axios.post<CreateEditResponse>(`${api}${TEMPLATES_CREATE}`, payload, config);

            setCreateEditResponse(data);
            getTemplates(ClassStatus.Draft);

            setCreateEditTemplateStatus(CreateStatus.SUCCESS);
            return CreateStatus.SUCCESS;
        } catch (err) {
            enqueueSnackbar(tCommon('somethingWentWrong'), { variant: 'error' });

            setCreateEditTemplateStatus(CreateStatus.ERROR);
            return CreateStatus.ERROR;
        }
    };

    const updateTemplate = async ({ sid, status, templateName, description }: UpdateTemplateInterface): Promise<CreateStatus> => {
        setCreateEditTemplateStatus(CreateStatus.LOADING);
        try {
            const config: AxiosRequestConfig = {
                headers: {
                    token: userUuid as string
                }
            };

            const payload = {
                sid,
                status,
                class_name: templateName,
                class_description: description
            };

            const { data } = await axios.post<CreateEditResponse>(`${api}${TEMPLATES_UPDATE}`, payload, config);

            setCreateEditResponse(data);

            if (status === ClassStatus.Published) getTemplates(ClassStatus.Published);
            if (status === ClassStatus.Draft) getTemplates(ClassStatus.Draft);

            setCreateEditTemplateStatus(CreateStatus.SUCCESS);
            return CreateStatus.SUCCESS;
        } catch (err) {
            enqueueSnackbar(tCommon('somethingWentWrong'), { variant: 'error' });

            setCreateEditTemplateStatus(CreateStatus.ERROR);
            return CreateStatus.ERROR;
        }
    };

    const deleteTemplate = async ({ templateId, onOptimisticUpdate, onError, onSuccess }: DeleteTemplateInterface) => {
        try {
            if (onOptimisticUpdate) {
                onOptimisticUpdate();
            }

            const config: AxiosRequestConfig = {
                headers: {
                    token: userUuid as string
                },
                params: { template_ids: [templateId].join('') }
            };

            await axios.delete(`${api}${TEMPLATES_DELETE}`, config);

            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            onError();
        }
    };

    const deleteDraftTemplate = async (templateId: number) => {
        const onOptimisticUpdate = () => {
            setDraftTemplates((previous) => previous.filter((template) => template.sid !== templateId));
        };
        const onError = () => {
            getTemplates(ClassStatus.Draft);
            enqueueSnackbar(tCommon('somethingWentWrong'), { variant: 'error' });
        };

        await deleteTemplate({ templateId, onOptimisticUpdate, onError });
    };

    const deletePublishedTemplate = async (templateId: number) => {
        const onOptimisticUpdate = () => {
            setPublishedTemplates((previous) => previous.filter((template) => template.sid !== templateId));
        };
        const onError = () => {
            getTemplates(ClassStatus.Published);
            enqueueSnackbar(tCommon('somethingWentWrong'), { variant: 'error' });
        };

        await deleteTemplate({ templateId, onOptimisticUpdate, onError });
    };

    const updateTemplateStatus = async ({ templateId, status, onOptimisticUpdate, onError, onSuccess }: UpdateTemplateStatusInterface) => {
        try {
            const config: AxiosRequestConfig = {
                headers: {
                    token: userUuid as string
                }
            };

            const payload = {
                sid: templateId,
                status
            };

            if (onOptimisticUpdate) {
                onOptimisticUpdate();
            }

            await axios.post(`${api}${TEMPLATES_UPDATE}`, payload, config);

            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            onError();
        }
    };

    const handleOptimisticPublish = (templateId: number) => {
        setDraftTemplates((previous) => previous.filter((template) => template.sid !== templateId));
        setPublishedTemplates((previous) => {
            const publishedTemplate = draftTemplates.find((template) => template.sid === templateId);

            if (publishedTemplate) {
                publishedTemplate.status = ClassStatus.Published;

                return [publishedTemplate, ...previous];
            }

            return previous;
        });
    };

    const handleOptimisticUnpublish = (templateId: number) => {
        setPublishedTemplates((previous) => previous.filter((template) => template.sid !== templateId));
        setDraftTemplates((previous) => {
            const draftTemplate = publishedTemplates.find((template) => template.sid === templateId);

            if (draftTemplate) {
                draftTemplate.status = ClassStatus.Draft;

                return [draftTemplate, ...previous];
            }

            return previous;
        });
    };

    const publishTemplate = async (templateId: number) => {
        const onOptimisticUpdate = () => handleOptimisticPublish(templateId);

        const onError = () => {
            getTemplates(ClassStatus.Draft);
            getTemplates(ClassStatus.Published);
            enqueueSnackbar(tCommon('somethingWentWrong'), { variant: 'error' });
        };

        updateTemplateStatus({ templateId, status: ClassStatus.Published, onOptimisticUpdate, onError });
    };

    const unpublishTemplate = async (templateId: number) => {
        const onOptimisticUpdate = () => handleOptimisticUnpublish(templateId);

        const onError = () => {
            getTemplates(ClassStatus.Draft);
            getTemplates(ClassStatus.Published);
            enqueueSnackbar(tCommon('somethingWentWrong'), { variant: 'error' });
        };

        updateTemplateStatus({ templateId, status: ClassStatus.Draft, onOptimisticUpdate, onError });
    };

    const saveAsPublishedTemplate = async (templateId: number) => {
        const onOptimisticUpdate = () => {
            setSaveAsPublishedTemplateStatus(CreateStatus.LOADING);

            const isChangingStatus = Boolean(draftTemplates.find((template) => template.sid === templateId));

            if (isChangingStatus) {
                handleOptimisticPublish(templateId);
            }
        };

        const onSuccess = () => {
            setSaveAsPublishedTemplateStatus(CreateStatus.SUCCESS);

            history.push(`/template-manager${search}`);

            enqueueSnackbar(t('template-published'), { variant: 'success' });
        };

        const onError = () => {
            setSaveAsPublishedTemplateStatus(CreateStatus.ERROR);
            enqueueSnackbar(tCommon('somethingWentWrong'), { variant: 'error' });
        };

        updateTemplateStatus({ templateId, status: ClassStatus.Published, onOptimisticUpdate, onSuccess, onError });
    };

    const saveAsDraftTemplate = async (templateId: number) => {
        const onOptimisticUpdate = () => {
            setSaveAsDraftTemplateStatus(CreateStatus.LOADING);

            const isChangingStatus = Boolean(publishedTemplates.find((template) => template.sid === templateId));

            if (isChangingStatus) {
                handleOptimisticUnpublish(templateId);
            }
        };

        const onSuccess = () => {
            setSaveAsDraftTemplateStatus(CreateStatus.SUCCESS);

            history.push(`/template-manager${search}`);

            enqueueSnackbar(t('template-saved-as-draft'), { variant: 'success' });
        };

        const onError = () => {
            setSaveAsDraftTemplateStatus(CreateStatus.ERROR);
            enqueueSnackbar(tCommon('somethingWentWrong'), { variant: 'error' });
        };

        updateTemplateStatus({ templateId, status: ClassStatus.Draft, onOptimisticUpdate, onSuccess, onError });
    };

    const getMoodleUrl = async (screen: number) => {
        try {
            const params = {
                screen,
                userUuid,
                templateId: createEditResponse?.sid
            };

            const headers = {
                token: userUuid as string
            };

            const { data } = await axios.get<any>(`${api}${MOODLE_TEMPLATE_URL}`, { params, headers });

            return data?.moodleUrl ?? null;
        } catch (err) {
            enqueueSnackbar(tCommon('somethingWentWrong'), { variant: 'error' });

            return null;
        }
    };

    const templateToEdit = useMemo(() => {
        if (templateEditId) {
            const publishedTemplate = publishedTemplates.find((template) => template.sid === templateEditId);
            if (publishedTemplate) {
                return publishedTemplate;
            }

            const draftTemplate = draftTemplates.find((template) => template.sid === templateEditId);
            if (draftTemplate) {
                return draftTemplate;
            }

            return null;
        }
        return null;
    }, [draftTemplates, publishedTemplates, templateEditId]);

    return {
        draftTemplates,
        publishedTemplates,

        createTemplate,
        createEditTemplateStatus,

        deleteDraftTemplate,
        deletePublishedTemplate,

        publishTemplate,
        unpublishTemplate,

        templateEditId,
        templateToEdit,
        setTemplateEditId,
        updateTemplate,

        getMoodleUrl,

        createEditResponse,

        saveAsPublishedTemplate,
        saveAsPublishedTemplateStatus,

        saveAsDraftTemplate,
        saveAsDraftTemplateStatus
    };
};

const TemplateContainer = createContainer(useTemplateContext);

export default TemplateContainer;
